import React, { useState, useEffect } from "react";
import { CheckCircle2 } from "lucide-react";
import { getMyPurchasesWithPrograms, createOrder, verifyPayment } from "../../api/api";
import PlanPaymentPopup from "../PlanPaymentPopup";
import PaymentPopup from "../DashboardLayouts/PaymentPopup";

const PurchasesPlans = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchaseData, setPurchaseData] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);

  // Payment related states
  const [processingPayment, setProcessingPayment] = useState(false);
  const [processingProgramId, setProcessingProgramId] = useState(null);
  const [showPlanPopup, setShowPlanPopup] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState({});

  useEffect(() => {
    fetchPurchases();
  }, []);

  // Load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onerror = () => {
        console.error("Failed to load Razorpay script");
        showToast('Failed to load payment gateway', 'error');
      };
      document.body.appendChild(script);
    };
    loadRazorpayScript();
  }, []);

  const showToast = (message, type = 'success') => {
    const existingToast = document.querySelector('.custom-toast');
    if (existingToast) existingToast.remove();

    const toast = document.createElement('div');
    toast.className = `custom-toast toast-${type}`;

    const icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ';
    const bgColor = type === 'success' ? '#10B981' : type === 'error' ? '#EF4444' : '#3B82F6';

    toast.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="background: rgba(255,255,255,0.2); width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold;">
          ${icon}
        </div>
        <div style="flex: 1;">${message}</div>
        <button onclick="this.parentElement.parentElement.remove()" style="background: none; border: none; color: white; cursor: pointer; font-size: 18px;">×</button>
      </div>
    `;

    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background-color: ${bgColor};
      color: white;
      padding: 14px 20px;
      border-radius: 12px;
      z-index: 9999;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1);
      min-width: 300px;
      max-width: 400px;
      cursor: pointer;
      animation: slideInRight 0.3s ease;
      z-index: 10000;
    `;

    if (!document.querySelector('#toast-styles')) {
      const style = document.createElement('style');
      style.id = 'toast-styles';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(toast);

    setTimeout(() => {
      if (toast && toast.parentElement) {
        toast.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => {
          if (toast && toast.parentElement) toast.remove();
        }, 300);
      }
    }, 3000);

    toast.onclick = () => {
      if (toast && toast.parentElement) {
        toast.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => {
          if (toast && toast.parentElement) toast.remove();
        }, 300);
      }
    };
  };

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const response = await getMyPurchasesWithPrograms();
      
      if (response.status) {
        setPurchaseData(response);
        setPrograms(response.data || []);
        setCurrentPlan(response.current_plan?.[0] || null);

        // Set default variants
        const initialVariants = {};
        (response.data || []).forEach(program => {
          if (program.ProgramVariants && program.ProgramVariants.length > 0) {
            initialVariants[program.id] = program.ProgramVariants[0];
          }
        });
        setSelectedVariants(initialVariants);

      } else {
        setError(response.message || "Failed to fetch purchases");
      }
    } catch (err) {
      console.error("Error fetching purchases:", err);
      setError(err.message || "Failed to fetch purchases");
    } finally {
      setLoading(false);
    }
  };

  // Format price with currency
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(parseFloat(price));
  };

  // Calculate price with GST
  const getPriceWithGST = (price, gstPercentage) => {
    const basePrice = parseFloat(price);
    if (!gstPercentage || gstPercentage === 0) return basePrice;
    const gstAmount = (basePrice * parseFloat(gstPercentage)) / 100;
    return basePrice + gstAmount;
  };

  // Get plan features
  const getPlanFeatures = (program) => {
    if (program.ProgramFeatures && program.ProgramFeatures.length > 0) {
      return program.ProgramFeatures.map(f => f.feature_text);
    }
    return [
      "Personalized meal plans",
      "Expert guidance",
      "Progress tracking",
      "Dedicated support"
    ];
  };

  // Check if user has active plan for this program
  const hasActivePlan = (program) => {
    return program.user_plan?.has_active_plan || false;
  };

  // Check if this program is the current active plan
  const isCurrentPlan = (program) => {
    return currentPlan && currentPlan.program_id === program.id;
  };

  // Get plan status
  const getPlanStatus = (program) => {
    if (isCurrentPlan(program)) {
      return {
        isActive: true,
        label: "Active",
        btnText: "Active Plan",
        btnClass: "bg-[#f4faf5] text-[#58b96b] cursor-default"
      };
    }
    if (hasActivePlan(program)) {
      return {
        isActive: false,
        label: "Purchased",
        btnText: "Active Plan",
        btnClass: "border border-[#dfe5df] text-[#7f8882] cursor-default"
      };
    }
    return {
      isActive: false,
      label: "Available",
      btnText: "Purchase Now",
      btnClass: "bg-[#58b96b] text-white hover:bg-[#4a9e5a]"
    };
  };

  // Handle variant change
  const handleVariantChange = (programId, variant) => {
    setSelectedVariants(prev => ({
      ...prev,
      [programId]: variant
    }));
  };

  // Handle purchase click
  const handlePurchaseClick = (program, variant) => {
    if (!variant) {
      showToast('Please select a plan duration', 'error');
      return;
    }
    setSelectedProgram(program);
    setSelectedVariant(variant);
    setShowPlanPopup(true);
  };

  // Handle plan proceed
  const handlePlanProceed = () => {
    setShowPlanPopup(false);
    setShowPaymentPopup(true);
  };

  // Process payment
  const processPayment = async () => {
    if (!selectedProgram || !selectedVariant) return;

    setProcessingPayment(true);
    setProcessingProgramId(selectedProgram.id);
    setShowPaymentPopup(false);

    try {
      const orderData = {
        program_id: selectedProgram.id,
        variant_id: selectedVariant.id,
        payment_method: "razorpay",
        auto_renew: false
      };

      const orderResponse = await createOrder(orderData);

      if (orderResponse?.status === true && orderResponse?.data) {
        const apiData = orderResponse.data;
        const razorpayOrderId = apiData.order_id;
        const razorpayKeyId = apiData.razorpay_key;
        const amountFromApi = apiData.amount;
        const purchaseId = apiData.purchase_id;

        if (razorpayOrderId && razorpayKeyId) {
          initializeRazorpayPayment(
            razorpayOrderId,
            razorpayKeyId,
            amountFromApi,
            selectedProgram,
            selectedVariant,
            purchaseId
          );
        } else {
          console.error('Missing Razorpay order details:', apiData);
          showToast('Failed to create payment order. Please try again.', 'error');
          setProcessingPayment(false);
          setProcessingProgramId(null);
        }
      } else {
        console.error('API returned unsuccessful response:', orderResponse);
        showToast(orderResponse?.message || 'Failed to create order. Please try again.', 'error');
        setProcessingPayment(false);
        setProcessingProgramId(null);
      }

    } catch (error) {
      console.error("Purchase error details:", error);
      if (error.response?.data?.message) {
        showToast(error.response.data.message, 'error');
      } else if (error.response?.status === 401) {
        showToast('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else {
        showToast(error.message || 'An unexpected error occurred.', 'error');
      }
      setProcessingPayment(false);
      setProcessingProgramId(null);
    }
  };

  // Initialize Razorpay payment
  const initializeRazorpayPayment = (razorpayOrderId, razorpayKeyId, amountInPaise, program, variant, purchaseId) => {
    if (window.Razorpay) {
      openRazorpay(razorpayOrderId, razorpayKeyId, amountInPaise, program, variant, purchaseId);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => openRazorpay(razorpayOrderId, razorpayKeyId, amountInPaise, program, variant, purchaseId);
    script.onerror = () => {
      showToast('Failed to load payment gateway. Please refresh the page.', 'error');
      setProcessingPayment(false);
      setProcessingProgramId(null);
    };
    document.body.appendChild(script);
  };

  // Open Razorpay
  const openRazorpay = (razorpayOrderId, razorpayKeyId, amountInPaise, program, variant, purchaseId) => {
    const userDataFromStorage = JSON.parse(localStorage.getItem('user') || '{}');

    const options = {
      key: razorpayKeyId,
      amount: amountInPaise,
      currency: 'INR',
      name: 'Nutrillion',
      description: `${program.name} - ${variant.duration}`,
      order_id: razorpayOrderId,
      prefill: {
        name: userDataFromStorage?.fullName || userDataFromStorage?.name || '',
        email: userDataFromStorage?.email || '',
        contact: userDataFromStorage?.phoneNumber || userDataFromStorage?.phone || ''
      },
      theme: {
        color: '#F59E0B'
      },
      modal: {
        ondismiss: () => {
          showToast('Payment cancelled', 'info');
          setProcessingPayment(false);
          setProcessingProgramId(null);
        }
      },
      handler: async (response) => {
        await verifyProgramPayment(response, razorpayOrderId, purchaseId);
      }
    };

    try {
      const razorpay = new window.Razorpay(options);

      razorpay.on('payment.failed', (response) => {
        console.error('Payment failed:', response);
        showToast(`Payment failed: ${response.error?.description || 'Please try again'}`, 'error');
        setProcessingPayment(false);
        setProcessingProgramId(null);
      });

      razorpay.open();
    } catch (error) {
      console.error('Razorpay initialization error:', error);
      showToast('Failed to initialize payment. Please try again.', 'error');
      setProcessingPayment(false);
      setProcessingProgramId(null);
    }
  };

  // Verify payment
  const verifyProgramPayment = async (paymentResponse, razorpayOrderId, purchaseId) => {
    showToast('Verifying payment...', 'info');

    try {
      const verificationData = {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        purchase_id: purchaseId
      };

      const verificationResult = await verifyPayment(verificationData);

      if (verificationResult?.status === true || verificationResult?.success === true) {
        showToast('Payment successful! Your purchase is confirmed.', 'success');
        setTimeout(() => {
          window.location.href = '/my-purchases';
        }, 2000);
      } else {
        showToast(verificationResult?.message || 'Payment verification failed. Please contact support.', 'error');
      }

    } catch (error) {
      console.error('Payment verification error:', error);
      showToast('Payment verification failed. Please contact support.', 'error');
    } finally {
      setProcessingPayment(false);
      setProcessingProgramId(null);
    }
  };

  // Close all popups
  const closeAllPopups = () => {
    setShowPlanPopup(false);
    setShowPaymentPopup(false);
    setSelectedProgram(null);
    setSelectedVariant(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px] w-full font-lato">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#58b96b] mx-auto"></div>
          <p className="mt-4 text-[#98a19b]">Loading purchases...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[400px] w-full font-lato">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ {error}</div>
          <button 
            onClick={fetchPurchases} 
            className="bg-[#58b96b] text-white px-6 py-2 rounded-full hover:bg-[#4a9e5a] transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full font-lato">
      {/* Toasts container */}
      <div id="toast-container"></div>

      <div className="mb-7">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] leading-tight">
          Purchases & Plans
        </h2>
        <p className="text-[#98a19b] text-sm mt-2">
          Manage your active subscriptions, view plan benefits, and explore new
          packages.
        </p>
        {/* {purchaseData?.user_purchases_summary && (
          <p className="text-[#98a19b] text-sm mt-1">
            Total Active Purchases: {purchaseData.user_purchases_summary.total_active_purchases}
          </p>
        )} */}
      </div>

      {/* Active Subscription Section */}
      <h3 className="text-xl sm:text-xl font-semibold text-[#24312d] mb-4">
        Active Subscription
      </h3>

      {currentPlan ? (
        <div className="bg-white border border-[#e8ece8] rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-6">
            <div>
              <div className="flex items-center gap-3 mb-5 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-[#58b96b] text-white text-[11px] font-medium">
                  {currentPlan.status === 'active' ? 'Active' : currentPlan.status}
                </span>
                <span className="text-[12px] text-[#98a19b]">
                  {currentPlan.start_date ? `Started: ${new Date(currentPlan.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}` : ''}
                </span>
              </div>

              <h4 className="text-2xl sm:text-3xl font-semibold text-[#24312d] leading-tight mb-3">
                {currentPlan.program_name}
              </h4>

              <p className="text-[14px] text-[#98a19b] leading-6 max-w-[640px] mb-6">
                {currentPlan.variant_duration} plan with personalized nutrition and health management.
              </p>

              <p className="text-[14px] font-semibold text-[#24312d] mb-4">
                Plan Includes:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentPlan.variant_details?.ProgramFeatures?.length > 0 ? (
                  currentPlan.variant_details.ProgramFeatures.map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 size={16} className="text-[#58b96b]" />
                      <span className="text-[14px] text-[#24312d]">{feature.feature_text}</span>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center gap-3 col-span-2">
                    <CheckCircle2 size={16} className="text-[#58b96b]" />
                    <span className="text-[14px] text-[#24312d]">Personalized nutrition plan</span>
                  </div>
                )}
              </div>
            </div>
            <div className="border border-[#edf1ed]  bg-[#f8faf8] rounded-xl p-5 h-fit">
              <div className="mb-6">
                <h4 className="text-2xl sm:text-3xl font-semibold text-[#24312d] leading-none">
                  {formatPrice(currentPlan.variant_price)}
                  <span className="text-[15px] font-normal text-[#98a19b]">
                    /{currentPlan.variant_duration?.split(' ')[0] || 'mo'}
                  </span>
                </h4>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between gap-4">
                  <span className="text-[13px] text-[#98a19b]">
                    Duration
                  </span>
                  <span className="text-[13px] font-medium text-[#24312d]">
                    {currentPlan.variant_duration || 'N/A'}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-[13px] text-[#98a19b]">
                    Status
                  </span>
                  <span className="text-[13px] font-medium text-[#24312d] capitalize">
                    {currentPlan.status || 'Active'}
                  </span>
                </div>

                {currentPlan.payment && (
                  <div className="flex justify-between gap-4">
                    <span className="text-[13px] text-[#98a19b]">
                      Payment Method
                    </span>
                    <span className="text-[13px] font-medium text-[#24312d]">
                      {currentPlan.payment.payment_method || 'N/A'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#e8ece8] rounded-xl p-6 mb-8 text-center">
          <p className="text-[#98a19b]">No active subscription found.</p>
        </div>
      )}

      {/* Available Plans Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <h3 className="text-xl sm:text-xl font-semibold text-[#24312d]">
          Available Plans
        </h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {programs.map((program) => {
          const status = getPlanStatus(program);
          const features = getPlanFeatures(program);
          const variants = program.ProgramVariants || [];
          const selectedVariant = selectedVariants[program.id] || variants[0];
          
          // Check if user already has active plan
          const hasActive = status.isActive || hasActivePlan(program);

          return (
            <div
              key={program.id}
              className={`bg-white rounded-xl p-6 border relative ${
                status.isActive ? "border-[#58b96b] shadow-sm" : "border-[#e8ece8]"
              }`}
            >
              {status.isActive && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#58b96b] text-white text-[11px] font-medium whitespace-nowrap">
                  Current Plan
                </span>
              )}

              <div className="text-center mb-4">
                <h4 className="text-xl sm:text-xl font-semibold text-[#24312d] mb-2">
                  {program.name}
                </h4>
                <p className="text-[12px] text-[#98a19b] leading-6 min-h-[48px]">
                  {program.description?.substring(0, 80)}...
                </p>
              </div>

              {/* Variant Selection Buttons - Only for available plans */}
              {variants.length > 0 && !hasActive && (
                <div className="flex flex-wrap justify-center gap-2 mt-2 mb-4">
                  {variants.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() => handleVariantChange(program.id, variant)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-all duration-300 ${
                        selectedVariant?.id === variant.id
                          ? "bg-yellow-600 text-white shadow-md"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      {variant.duration}
                    </button>
                  ))}
                </div>
              )}

              {/* Price based on selected variant - For available plans */}
              {selectedVariant && !hasActive && (
                <div className="text-center mb-4">
                  <span className="text-2xl font-bold text-yellow-600">
                    {formatPrice(getPriceWithGST(
                      selectedVariant.price,
                      selectedVariant.gst_percentage
                    ))}
                  </span>
                  <span className="text-gray-600 ml-1 text-sm">INR</span>
                  {selectedVariant.gst_percentage > 0 && (
                    <span className="text-xs text-gray-500 ml-2">
                      (Incl. GST {selectedVariant.gst_percentage}%)
                    </span>
                  )}
                  <span className="text-[15px] text-[#98a19b] ml-2">
                    /{selectedVariant.duration?.split(' ')[0] || 'mo'}
                  </span>
                </div>
              )}

              {/* Price for active/purchased plans */}
              {hasActive && (
                <div className="text-center mb-4">
                  <span className="text-2xl font-bold text-[#24312d]">
                    {formatPrice(
                      status.isActive 
                        ? currentPlan?.variant_price 
                        : program.user_plan?.price || variants[0]?.price
                    )}
                  </span>
                  <span className="text-gray-600 ml-1 text-sm">INR</span>
                  <span className="text-[15px] text-[#98a19b] ml-2">
                    /{status.isActive 
                      ? currentPlan?.variant_duration?.split(' ')[0] || 'mo'
                      : program.user_plan?.duration?.split(' ')[0] || variants[0]?.duration?.split(' ')[0] || 'mo'
                    }
                  </span>
                </div>
              )}

              {/* Features */}
              <div className="border-t border-[#edf1ed] pt-4 space-y-3 mb-5">
                {features.slice(0, 4).map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 size={14} className="text-[#58b96b] flex-shrink-0" />
                    <span className="text-[13px] text-[#24312d]">
                      {item.length > 60 ? item.substring(0, 60) + '...' : item}
                    </span>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <button
                className={`w-full h-[42px] rounded-md text-[13px] font-medium transition-colors ${
                  hasActive 
                    ? status.isActive 
                      ? "bg-[#f4faf5] text-[#58b96b] cursor-default"
                      : "border border-[#dfe5df] text-[#7f8882] cursor-default"
                    : "bg-[#58b96b] text-white hover:bg-[#4a9e5a]"
                }`}
                onClick={() => {
                  if (!hasActive) {
                    handlePurchaseClick(program, selectedVariant);
                  }
                }}
                disabled={hasActive || (processingPayment && processingProgramId === program.id)}
              >
                {processingPayment && processingProgramId === program.id ? (
                  <>
                    <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></span>
                    Processing...
                  </>
                ) : (
                  status.btnText
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* STEP 1: Plan Payment Popup */}
      <PlanPaymentPopup
        open={showPlanPopup}
        onClose={closeAllPopups}
        program={selectedProgram}
        variant={selectedVariant}
        onProceed={handlePlanProceed}
        formatPrice={formatPrice}
        getPriceWithGST={getPriceWithGST}
      />

      {/* STEP 2: Payment Popup */}
      <PaymentPopup
        open={showPaymentPopup}
        onClose={closeAllPopups}
        program={selectedProgram}
        variant={selectedVariant}
        formatPrice={formatPrice}
        getPriceWithGST={getPriceWithGST}
        onPaymentSuccess={() => {
          showToast('Payment successful! Your purchase is confirmed.', 'success');
          setTimeout(() => {
            window.location.href = '/my-purchases';
          }, 2000);
        }}
      />
    </div>
  );
};

export default PurchasesPlans;